<template>
    <div>
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t('dashboard_v2') }}</h6>
                    <nav
                        aria-label="breadcrumb"
                        class="d-none d-md-inline-block ml-md-4"
                    >
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3 col-sm-12">
                    <base-input>
                        <flat-picker
                            slot-scope="{ focus, blur }"
                            @on-open="focus"
                            @on-close="blur"
                            :config="{ allowInput: true }"
                            class="form-control datepicker"
                            v-model="queryDate"
                        >
                        </flat-picker>
                    </base-input>
                </div>
                <div class="col-md-3 col-sm-12">
                    <base-input>
                        <el-select v-model="dateType" :placeholder="$t('date_type')" @change="handleSearch()">
                            <el-option :label="$t('created_date')"  value="createddate"></el-option>
                            <el-option :label="$t('packing_date')"  value="packingdate"></el-option>
                        </el-select>
                    </base-input>
                </div>
                <div class="col-md-6 col-sm-12">
                    <base-button
                        size="md"
                        type="primary"
                        @click="handleSearch()"
                        :loading="loading"
                    >
                        {{ $t('search') }}
                    </base-button>
                    <base-button
                        size="md"
                        type="secondary"
                        @click="handleForceRefresh()"
                        :loading="loading"
                    >
                        {{ $t('refresh') }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- Summary Stats Cards -->
            <div class="row" v-if="totalSummary">
                <div class="col-xl-3 col-md-6">
                    <stats-card
                        :title="$t('total_orders')"
                        type="gradient-info"
                        :sub-title="totalOrders.toString()"
                        icon="ni ni-chart-bar-32"
                    >
                    </stats-card>
                </div>
                <div class="col-xl-3 col-md-6">
                    <stats-card
                        :title="$t('total_amount')"
                        type="gradient-green"
                        :sub-title="formatCurrency(totalSummary.sum_amount)"
                        icon="ni ni-money-coins"
                    >
                    </stats-card>
                </div>
                <div class="col-xl-3 col-md-6">
                    <stats-card
                        :title="$t('total_pay_amount')"
                        type="gradient-primary"
                        :sub-title="formatCurrency(totalSummary.sum_pay_amount)"
                        icon="fa fa-credit-card"
                    >
                    </stats-card>
                </div>
                <div class="col-xl-3 col-md-6">
                    <stats-card
                        :title="$t('order_status_completed')"
                        type="gradient-success"
                        :sub-title="(totalSummary.count_completed || 0).toString()"
                        icon="fa fa-check"
                    >
                    </stats-card>
                </div>
            </div>

            <!-- Tabs -->
            <div class="row">
                <div class="col-12">
                    <card class="no-border-card" body-classes="px-0 pb-1">
                        <el-tabs v-model="activeTab" type="card">
                            <!-- By Platform Tab -->
                            <el-tab-pane :label="$t('tab_by_platform')" name="platform">
                                <div class="px-3 py-2 d-flex flex-wrap">
                                    <base-button size="sm" type="info" @click="showChart('platform')">
                                        <i class="fa fa-chart-bar"></i> {{ $t('chart_by_count') }}
                                    </base-button>
                                    <base-button size="sm" type="primary" @click="showChart('platform_amount')" class="ml-2">
                                        <i class="fa fa-chart-bar"></i> {{ $t('chart_by_amount') }}
                                    </base-button>
                                    <base-button size="sm" :type="showStatusColumns ? 'warning' : 'default'" @click="showStatusColumns = !showStatusColumns" class="ml-2">
                                        <i class="fa fa-columns"></i> {{ showStatusColumns ? $t('hide_status_columns') : $t('show_status_columns') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('platform')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedPlatform"
                                    row-key="platform_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'platform')"
                                >
                                    <el-table-column :label="$t('platform')" prop="platform_name" min-width="120" sortable="custom" ></el-table-column>
                                    <el-table-column :label="$t('count_all')" prop="count_all" min-width="80" sortable="custom">
                                        <template slot-scope="scope">{{ countAll(scope.row.summary) }}</template>
                                    </el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_unpaid')" prop="summary.count_unpaid" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_shipment')" prop="summary.count_awaiting_shipment" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaitingcollection')" prop="summary.count_awaiting_collection" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_transit')" prop="summary.count_awaiting_transit" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_intransit')" prop="summary.count_in_transit" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_delivered')" prop="summary.count_delivered" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_completed')" prop="summary.count_completed" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_cancelled')" prop="summary.count_cancelled" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_returning')" prop="summary.count_returning" min-width="90" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('total_amount')" prop="summary.sum_amount" min-width="130" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ formatCurrency(scope.row.summary.sum_amount) }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('total_pay_amount')" prop="summary.sum_pay_amount" min-width="130" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ formatCurrency(scope.row.summary.sum_pay_amount) }}
                                        </template>
                                    </el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byPlatform.length > pageSize"
                                    :total="byPlatform.length"
                                    :per-page="pageSize"
                                    v-model="platformPage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>

                            <!-- By Channel Tab -->
                            <el-tab-pane :label="$t('tab_by_channel')" name="channel">
                                <div class="px-3 py-2 d-flex flex-wrap">
                                    <base-button size="sm" type="info" @click="showChart('channel')">
                                        <i class="fa fa-chart-bar"></i> {{ $t('chart_by_count') }}
                                    </base-button>
                                    <base-button size="sm" type="primary" @click="showChart('channel_amount')" class="ml-2">
                                        <i class="fa fa-chart-bar"></i> {{ $t('chart_by_amount') }}
                                    </base-button>
                                    <base-button size="sm" :type="showStatusColumns ? 'warning' : 'default'" @click="showStatusColumns = !showStatusColumns" class="ml-2">
                                        <i class="fa fa-columns"></i> {{ showStatusColumns ? $t('hide_status_columns') : $t('show_status_columns') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('channel')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedChannel"
                                    row-key="channel_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'channel')"
                                >
                                    <el-table-column :label="$t('channel')" prop="channel_name" min-width="120" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ scope.row.channel_name || 'Channel ' + scope.row.channel_id }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('platform')" prop="platform_name" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('count_all')" prop="count_all" min-width="80" sortable="custom">
                                        <template slot-scope="scope">{{ countAll(scope.row.summary) }}</template>
                                    </el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_unpaid')" prop="summary.count_unpaid" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_shipment')" prop="summary.count_awaiting_shipment" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaitingcollection')" prop="summary.count_awaiting_collection" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_transit')" prop="summary.count_awaiting_transit" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_intransit')" prop="summary.count_in_transit" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_delivered')" prop="summary.count_delivered" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_completed')" prop="summary.count_completed" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_cancelled')" prop="summary.count_cancelled" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_returning')" prop="summary.count_returning" min-width="90" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('total_amount')" prop="summary.sum_amount" min-width="130" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ formatCurrency(scope.row.summary.sum_amount) }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('total_pay_amount')" prop="summary.sum_pay_amount" min-width="130" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ formatCurrency(scope.row.summary.sum_pay_amount) }}
                                        </template>
                                    </el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byChannel.length > pageSize"
                                    :total="byChannel.length"
                                    :per-page="pageSize"
                                    v-model="channelPage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>

                            <!-- By User Tab -->
                            <el-tab-pane :label="$t('tab_by_user')" name="user">
                                <div class="px-3 py-2 d-flex flex-wrap">
                                    <base-button size="sm" type="info" @click="showChart('user')">
                                        <i class="fa fa-chart-bar"></i> {{ $t('chart_by_count') }}
                                    </base-button>
                                    <base-button size="sm" type="primary" @click="showChart('user_amount')" class="ml-2">
                                        <i class="fa fa-chart-bar"></i> {{ $t('chart_by_amount') }}
                                    </base-button>
                                    <base-button size="sm" :type="showStatusColumns ? 'warning' : 'default'" @click="showStatusColumns = !showStatusColumns" class="ml-2">
                                        <i class="fa fa-columns"></i> {{ showStatusColumns ? $t('hide_status_columns') : $t('show_status_columns') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('user')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedUser"
                                    row-key="user_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'user')"
                                >
                                    <el-table-column :label="$t('user')" prop="user_name" min-width="180" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ scope.row.user_name || 'User ' + scope.row.user_id }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('count_all')" prop="count_all" min-width="80" sortable="custom">
                                        <template slot-scope="scope">{{ countAll(scope.row.summary) }}</template>
                                    </el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_unpaid')" prop="summary.count_unpaid" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_shipment')" prop="summary.count_awaiting_shipment" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaitingcollection')" prop="summary.count_awaiting_collection" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_transit')" prop="summary.count_awaiting_transit" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_intransit')" prop="summary.count_in_transit" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_delivered')" prop="summary.count_delivered" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_completed')" prop="summary.count_completed" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_cancelled')" prop="summary.count_cancelled" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_returning')" prop="summary.count_returning" min-width="90" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('total_amount')" prop="summary.sum_amount" min-width="130" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ formatCurrency(scope.row.summary.sum_amount) }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('total_pay_amount')" prop="summary.sum_pay_amount" min-width="130" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ formatCurrency(scope.row.summary.sum_pay_amount) }}
                                        </template>
                                    </el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byUserWhoKey.length > pageSize"
                                    :total="byUserWhoKey.length"
                                    :per-page="pageSize"
                                    v-model="userPage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>

                            <!-- Top Products Tab -->
                            <el-tab-pane :label="$t('tab_top_products')" name="products">
                                <div class="px-3 py-2 d-flex flex-wrap">
                                    <base-button size="sm" type="info" @click="showChart('products')">
                                        <i class="fa fa-chart-bar"></i> {{ $t('show_chart') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('products')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedProducts"
                                    row-key="bo_product_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'products')"
                                >
                                    <el-table-column :label="$t('product')" prop="bo_product_name" min-width="250" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('platform')" prop="platform_name" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('quantity')" prop="sum_quantity" min-width="100" sortable="custom"></el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byPromotionMostSell.length > pageSize"
                                    :total="byPromotionMostSell.length"
                                    :per-page="pageSize"
                                    v-model="productsPage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>

                            <!-- Top WMS Products Tab -->
                            <el-tab-pane :label="$t('tab_top_wms')" name="wms">
                                <div class="px-3 py-2 d-flex flex-wrap">
                                    <base-button size="sm" type="info" @click="showChart('wms')">
                                        <i class="fa fa-chart-bar"></i> {{ $t('show_chart') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('wms')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedWms"
                                    row-key="wms_product_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'wms')"
                                >
                                    <el-table-column :label="$t('wms_product')" prop="wms_product_name" min-width="200" sortable="custom"></el-table-column>
                                    <el-table-column :label="$t('quantity')" prop="sum_quantity" min-width="100" sortable="custom"></el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byWmsMostSell.length > pageSize"
                                    :total="byWmsMostSell.length"
                                    :per-page="pageSize"
                                    v-model="wmsPage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>

                            <!-- By Packer Tab -->
                            <el-tab-pane :label="$t('tab_by_packer')" name="packer">
                                <div class="px-3 py-2 d-flex">
                                    <base-button size="sm" type="info" @click="showChart('packer')">
                                        <i class="fa fa-chart-bar"></i> {{ $t('show_chart') }}
                                    </base-button>
                                    <base-button size="sm" :type="showStatusColumns ? 'warning' : 'default'" @click="showStatusColumns = !showStatusColumns" class="ml-2">
                                        <i class="fa fa-columns"></i> {{ showStatusColumns ? $t('hide_status_columns') : $t('show_status_columns') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('packer')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedPacker"
                                    row-key="packer_user_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'packer')"
                                >
                                    <el-table-column :label="$t('packer')" prop="packer_user_name" min-width="150" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ scope.row.packer_user_name || 'Packer ' + scope.row.packer_user_id }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('count_all')" prop="count_all" min-width="80" sortable="custom">
                                        <template slot-scope="scope">{{ countAll(scope.row.summary) }}</template>
                                    </el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_unpaid')" prop="summary.count_unpaid" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_shipment')" prop="summary.count_awaiting_shipment" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaitingcollection')" prop="summary.count_awaiting_collection" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_transit')" prop="summary.count_awaiting_transit" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_intransit')" prop="summary.count_in_transit" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_delivered')" prop="summary.count_delivered" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_completed')" prop="summary.count_completed" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_cancelled')" prop="summary.count_cancelled" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_returning')" prop="summary.count_returning" min-width="90" sortable="custom"></el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byProductDependOnPacker.length > pageSize"
                                    :total="byProductDependOnPacker.length"
                                    :per-page="pageSize"
                                    v-model="packerPage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>

                            <!-- By Packer Storage Tab -->
                            <el-tab-pane :label="$t('tab_by_packer_storage')" name="packer_storage">
                                <div class="px-3 py-2 d-flex">
                                    <base-button size="sm" :type="showStatusColumns ? 'warning' : 'default'" @click="showStatusColumns = !showStatusColumns" class="ml-2">
                                        <i class="fa fa-columns"></i> {{ showStatusColumns ? $t('hide_status_columns') : $t('show_status_columns') }}
                                    </base-button>
                                    <base-button size="sm" type="success" @click="exportTable('packer_storage')" class="ml-2">
                                        <i class="fa fa-file-excel"></i> {{ $t('export') }}
                                    </base-button>
                                </div>
                                <el-table
                                    :data="paginatedPackerStorage"
                                    row-key="storage_id"
                                    header-row-class-name="thead-light"
                                    :empty-text="$t('no_data')"
                                    @sort-change="(e) => handleSortChange(e, 'packer_storage')"
                                >
                                    <el-table-column :label="$t('storage')" prop="storage_name" min-width="150" sortable="custom">
                                        <template slot-scope="scope">
                                            {{ scope.row.storage_name || ('Storage ' + scope.row.storage_id) }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column :label="$t('count_all')" prop="count_all" min-width="80" sortable="custom">
                                        <template slot-scope="scope">{{ countAll(scope.row.summary) }}</template>
                                    </el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_unpaid')" prop="summary.count_unpaid" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_shipment')" prop="summary.count_awaiting_shipment" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaitingcollection')" prop="summary.count_awaiting_collection" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_awaiting_transit')" prop="summary.count_awaiting_transit" min-width="100" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_intransit')" prop="summary.count_in_transit" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_delivered')" prop="summary.count_delivered" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_completed')" prop="summary.count_completed" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_cancelled')" prop="summary.count_cancelled" min-width="80" sortable="custom"></el-table-column>
                                    <el-table-column v-if="showStatusColumns" :label="$t('order_status_returning')" prop="summary.count_returning" min-width="90" sortable="custom"></el-table-column>
                                </el-table>
                                <base-pagination
                                    v-if="byPackerStorage.length > pageSize"
                                    :total="byPackerStorage.length"
                                    :per-page="pageSize"
                                    v-model="packerStoragePage"
                                    align="center"
                                    class="mt-3"
                                ></base-pagination>
                            </el-tab-pane>
                        </el-tabs>
                    </card>
                </div>
            </div>

            <!-- Chart Modal -->
            <modal :show.sync="chartModal" modal-classes="modal-fullscreen-custom">
                <template slot="header">
                    <h5 class="modal-title">{{ chartTitle }} - {{ queryDate }}</h5>
                </template>
                <div class="chart-container-fullscreen">
                    <canvas ref="chartCanvas"></canvas>
                </div>
                <template slot="footer">
                    <base-button type="primary" @click="saveChartImage">
                        <i class="fa fa-download"></i> {{ $t('save_image') }}
                    </base-button>
                    <base-button type="secondary" @click="chartModal = false">{{ $t('close') }}</base-button>
                </template>
            </modal>
        </div>
    </div>
</template>
<script>
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import StatsCard from "@/components/Cards/StatsCard";
import { Table, TableColumn, Tabs, TabPane, Select, Option } from "element-ui";
import { mapActions } from "vuex";
import moment from "moment";
import swal from "sweetalert2";
import Chart from "chart.js";
import Modal from "@/components/Modal";
import BasePagination from "@/components/BasePagination";

export default {
    components: {
        StatsCard,
        flatPicker,
        Modal,
        BasePagination,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Select.name]: Select,
        [Option.name]: Option
    },
    data() {
        return {
            queryDate: "",
            dateType: "createddate",
            loading: false,
            activeTab: "platform",
            pageSize: 10,
            // Data
            byPlatform: [],
            byChannel: [],
            byUserWhoKey: [],
            byPromotionMostSell: [],
            byWmsMostSell: [],
            byProductDependOnPacker: [],
            byPackerStorage: [],
            totalSummary: null,
            // Pagination
            platformPage: 1,
            channelPage: 1,
            userPage: 1,
            productsPage: 1,
            wmsPage: 1,
            packerPage: 1,
            packerStoragePage: 1,
            // Sorting - default sort by count_all descending (products/wms by quantity)
            sortStates: {
                platform: { prop: 'count_all', order: 'descending' },
                channel: { prop: 'count_all', order: 'descending' },
                user: { prop: 'count_all', order: 'descending' },
                products: { prop: 'sum_quantity', order: 'descending' },
                wms: { prop: 'sum_quantity', order: 'descending' },
                packer: { prop: 'count_all', order: 'descending' },
                packer_storage: { prop: 'count_all', order: 'descending' }
            },
            // Chart
            chartModal: false,
            chartTitle: "",
            chartInstance: null,
            // Column visibility
            showStatusColumns: false
        };
    },
    computed: {
        totalOrders() {
            if (!this.totalSummary) return 0;
            return (
                (this.totalSummary.count_unpaid || 0) +
                (this.totalSummary.count_awaiting_shipment || 0) +
                (this.totalSummary.count_awaiting_collection || 0) +
                (this.totalSummary.count_partially_shipping || 0) +
                (this.totalSummary.count_awaiting_transit || 0) +
                (this.totalSummary.count_in_transit || 0) +
                (this.totalSummary.count_delivered || 0) +
                (this.totalSummary.count_completed || 0) +
                (this.totalSummary.count_cancelled || 0) +
                (this.totalSummary.count_returned || 0) +
                (this.totalSummary.count_claim || 0) +
                (this.totalSummary.count_returning || 0)
            );
        },
        // Sorted and Paginated data
        sortedPlatform() {
            return this.sortData(this.byPlatform.filter(x => x.platform_id !== 0), this.sortStates.platform);
        },
        paginatedPlatform() {
            return this.paginateData(this.sortedPlatform, this.platformPage);
        },
        sortedChannel() {
            return this.sortData(this.byChannel.filter(x => x.channel_id !== 0), this.sortStates.channel);
        },
        paginatedChannel() {
            return this.paginateData(this.sortedChannel, this.channelPage);
        },
        sortedUser() {
            return this.sortData(this.byUserWhoKey.filter(x => x.user_id !== 0), this.sortStates.user);
        },
        paginatedUser() {
            return this.paginateData(this.sortedUser, this.userPage);
        },
        sortedProducts() {
            return this.sortData(this.byPromotionMostSell.filter(x => x.bo_product_id !== 0), this.sortStates.products);
        },
        paginatedProducts() {
            return this.paginateData(this.sortedProducts, this.productsPage);
        },
        sortedWms() {
            return this.sortData(this.byWmsMostSell.filter(x => x.wms_product_id !== 0), this.sortStates.wms);
        },
        paginatedWms() {
            return this.paginateData(this.sortedWms, this.wmsPage);
        },
        sortedPacker() {
            return this.sortData(this.byProductDependOnPacker.filter(x => x.packer_user_id !== 0), this.sortStates.packer);
        },
        paginatedPacker() {
            return this.paginateData(this.sortedPacker, this.packerPage);
        },
        sortedPackerStorage() {
            return this.sortData(this.byPackerStorage.filter(x => x.storage_id !== 0), this.sortStates.packer_storage);
        },
        paginatedPackerStorage() {
            return this.paginateData(this.sortedPackerStorage, this.packerStoragePage);
        }
    },
    methods: {
        ...mapActions({
            listData: "Report/listReportDashboardByPlatformChannel"
        }),
        formatCurrency(value) {
            if (value == null) return "0.00";
            return new Intl.NumberFormat('th-TH', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        },
        countAll(summary) {
            if (!summary) return 0;
            return (
                (summary.count_unpaid || 0) +
                (summary.count_awaiting_shipment || 0) +
                (summary.count_awaiting_collection || 0) +
                (summary.count_partially_shipping || 0) +
                (summary.count_awaiting_transit || 0) +
                (summary.count_in_transit || 0) +
                (summary.count_delivered || 0) +
                (summary.count_completed || 0) +
                (summary.count_cancelled || 0) +
                (summary.count_returned || 0) +
                (summary.count_claim || 0) +
                (summary.count_returning || 0)
            );
        },
        getNestedValue(obj, path) {
            return path.split('.').reduce((acc, part) => acc && acc[part], obj);
        },
        sortData(data, sortState) {
            if (!sortState.prop || !sortState.order) {
                return data;
            }
            return [...data].sort((a, b) => {
                let aVal, bVal;
                // Handle count_all as special case (computed from summary)
                if (sortState.prop === 'count_all') {
                    aVal = this.countAll(a.summary);
                    bVal = this.countAll(b.summary);
                } else {
                    aVal = this.getNestedValue(a, sortState.prop);
                    bVal = this.getNestedValue(b, sortState.prop);
                }
                if (typeof aVal === 'string') {
                    aVal = aVal || '';
                    bVal = bVal || '';
                    return sortState.order === 'ascending'
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }
                aVal = aVal || 0;
                bVal = bVal || 0;
                return sortState.order === 'ascending' ? aVal - bVal : bVal - aVal;
            });
        },
        paginateData(data, page) {
            const start = (page - 1) * this.pageSize;
            const end = start + this.pageSize;
            return data.slice(start, end);
        },
        handleSortChange({ prop, order }, type) {
            this.sortStates[type] = { prop, order };
        },
        async handleSearch(forceRead = false) {
            this.loading = true;
            let params = {
                date: this.queryDate,
                date_type: this.dateType,
            };
            if (forceRead) {
                params.force_read = 1;
            }

            try {
                let response = await this.listData(params);
                let data = response.data.Datum;

                this.byPlatform = data.by_platform || [];
                this.byChannel = data.by_channel || [];
                this.byUserWhoKey = data.by_user_who_key || [];
                this.byPromotionMostSell = data.by_promotion_most_sell || [];
                this.byWmsMostSell = data.by_wms_most_sell || [];
                this.byProductDependOnPacker = data.by_product_depend_on_packer || [];
                this.byPackerStorage = data.by_packer_storage || [];

                // Reset pagination
                this.platformPage = 1;
                this.channelPage = 1;
                this.userPage = 1;
                this.productsPage = 1;
                this.wmsPage = 1;
                this.packerPage = 1;
                this.packerStoragePage = 1;

                this.calculateTotalSummary();
            } catch (err) {
                console.log(err);
                swal.fire({
                    text: (err.response && err.response.data && err.response.data.Msg) || err.message,
                    title: "Error",
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
            } finally {
                this.loading = false;
            }
        },
        handleForceRefresh() {
            this.handleSearch(true);
        },
        calculateTotalSummary() {
            if (!this.byPlatform || this.byPlatform.length === 0) {
                this.totalSummary = null;
                return;
            }

            this.totalSummary = this.byPlatform.reduce((acc, platform) => {
                const s = platform.summary || {};
                return {
                    count_unpaid: (acc.count_unpaid || 0) + (s.count_unpaid || 0),
                    count_awaiting_shipment: (acc.count_awaiting_shipment || 0) + (s.count_awaiting_shipment || 0),
                    count_awaiting_collection: (acc.count_awaiting_collection || 0) + (s.count_awaiting_collection || 0),
                    count_partially_shipping: (acc.count_partially_shipping || 0) + (s.count_partially_shipping || 0),
                    count_awaiting_transit: (acc.count_awaiting_transit || 0) + (s.count_awaiting_transit || 0),
                    count_in_transit: (acc.count_in_transit || 0) + (s.count_in_transit || 0),
                    count_delivered: (acc.count_delivered || 0) + (s.count_delivered || 0),
                    count_completed: (acc.count_completed || 0) + (s.count_completed || 0),
                    count_cancelled: (acc.count_cancelled || 0) + (s.count_cancelled || 0),
                    count_returned: (acc.count_returned || 0) + (s.count_returned || 0),
                    count_claim: (acc.count_claim || 0) + (s.count_claim || 0),
                    count_returning: (acc.count_returning || 0) + (s.count_returning || 0),
                    sum_amount: (acc.sum_amount || 0) + (s.sum_amount || 0),
                    sum_pay_amount: (acc.sum_pay_amount || 0) + (s.sum_pay_amount || 0)
                };
            }, {});
        },
        showChart(type) {
            let labels = [];
            let dataValues = [];
            let title = "";

            switch (type) {
                case 'platform':
                    title = this.$t('chart_by_platform') + ' (' + this.$t('count_all') + ')';
                    this.paginatedPlatform.forEach(item => {
                        labels.push(item.platform_name || 'Platform');
                        dataValues.push(this.countAll(item.summary));
                    });
                    break;
                case 'platform_amount':
                    title = this.$t('chart_by_platform') + ' (' + this.$t('total_pay_amount') + ')';
                    this.paginatedPlatform.forEach(item => {
                        labels.push(item.platform_name || 'Platform');
                        dataValues.push((item.summary && item.summary.sum_pay_amount) || 0);
                    });
                    break;
                case 'channel':
                    title = this.$t('chart_by_channel') + ' (' + this.$t('count_all') + ')';
                    this.paginatedChannel.forEach(item => {
                        labels.push(item.channel_name || 'Channel ' + item.channel_id);
                        dataValues.push(this.countAll(item.summary));
                    });
                    break;
                case 'channel_amount':
                    title = this.$t('chart_by_channel') + ' (' + this.$t('total_pay_amount') + ')';
                    this.paginatedChannel.forEach(item => {
                        labels.push(item.channel_name || 'Channel ' + item.channel_id);
                        dataValues.push((item.summary && item.summary.sum_pay_amount) || 0);
                    });
                    break;
                case 'user':
                    title = this.$t('chart_by_user') + ' (' + this.$t('count_all') + ')';
                    this.paginatedUser.forEach(item => {
                        labels.push(item.user_name || 'User ' + item.user_id);
                        dataValues.push(this.countAll(item.summary));
                    });
                    break;
                case 'user_amount':
                    title = this.$t('chart_by_user') + ' (' + this.$t('total_pay_amount') + ')';
                    this.paginatedUser.forEach(item => {
                        labels.push(item.user_name || 'User ' + item.user_id);
                        dataValues.push((item.summary && item.summary.sum_pay_amount) || 0);
                    });
                    break;
                case 'products':
                    title = this.$t('chart_top_products');
                    this.paginatedProducts.forEach(item => {
                        let name = item.bo_product_name || 'Product';
                        labels.push(name.length > 30 ? name.substring(0, 30) + '...' : name);
                        dataValues.push(item.sum_quantity || 0);
                    });
                    break;
                case 'wms':
                    title = this.$t('chart_top_wms');
                    this.paginatedWms.forEach(item => {
                        labels.push(item.wms_product_name || 'WMS Product');
                        dataValues.push(item.sum_quantity || 0);
                    });
                    break;
                case 'packer':
                    title = this.$t('chart_by_packer');
                    this.paginatedPacker.forEach(item => {
                        labels.push(item.packer_user_name || 'Packer ' + item.packer_user_id);
                        dataValues.push(this.countAll(item.summary));
                    });
                    break;
            }

            this.chartTitle = title;
            this.chartModal = true;

            this.$nextTick(() => {
                this.renderChart(labels, dataValues, type);
            });
        },
        renderChart(labels, dataValues, type) {
            if (this.chartInstance) {
                this.chartInstance.destroy();
            }

            const ctx = this.$refs.chartCanvas.getContext('2d');
            const colors = [
                '#5e72e4', '#2dce89', '#fb6340', '#f5365c', '#11cdef',
                '#172b4d', '#8965e0', '#ffc107', '#e14eca', '#23a2f6'
            ];

            const isHorizontal = type === 'products' || type === 'wms';
            const isAmount = type.endsWith('_amount');
            const self = this;

            // Ensure data values are numbers (not null/undefined)
            const cleanDataValues = dataValues.map(v => v || 0);

            // Determine label based on type
            let chartLabel = this.$t('count_all');
            if (type === 'products' || type === 'wms') {
                chartLabel = this.$t('quantity');
            } else if (isAmount) {
                chartLabel = this.$t('total_pay_amount');
            }

            this.chartInstance = new Chart(ctx, {
                type: isHorizontal ? 'horizontalBar' : 'bar',
                data: {
                    labels: labels,
                    datasets: [{
                        label: chartLabel,
                        data: cleanDataValues,
                        backgroundColor: colors.slice(0, labels.length),
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                        display: false
                    },
                    scales: {
                        xAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: function(value) {
                                    // For horizontal bar, X axis shows values
                                    if (isHorizontal && isAmount) {
                                        return self.formatCurrency(value);
                                    }
                                    return value;
                                }
                            }
                        }],
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: function(value) {
                                    // For vertical bar, Y axis shows values
                                    if (!isHorizontal && isAmount) {
                                        return self.formatCurrency(value);
                                    }
                                    return value;
                                }
                            }
                        }]
                    },
                    tooltips: {
                        callbacks: {
                            label: function(tooltipItem) {
                                if (isAmount) {
                                    return chartLabel + ': ' + self.formatCurrency(tooltipItem.value);
                                }
                                return chartLabel + ': ' + tooltipItem.value;
                            }
                        }
                    },
                    animation: {
                        onComplete: function() {
                            var chartInstance = this;
                            var ctx = chartInstance.ctx;
                            ctx.font = Chart.helpers.fontString(11, 'bold', Chart.defaults.global.defaultFontFamily);
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';

                            this.data.datasets.forEach(function(dataset, i) {
                                var meta = chartInstance.controller.getDatasetMeta(i);
                                meta.data.forEach(function(bar, index) {
                                    var data = dataset.data[index];
                                    var displayValue = isAmount ? self.formatCurrency(data) : data;

                                    if (isHorizontal) {
                                        // For horizontal bar, show text at end of bar
                                        ctx.fillStyle = '#333';
                                        ctx.textAlign = 'left';
                                        ctx.fillText(displayValue, bar._model.x + 5, bar._model.y);
                                    } else {
                                        // For vertical bar, show text above bar
                                        ctx.fillStyle = '#333';
                                        ctx.fillText(displayValue, bar._model.x, bar._model.y - 10);
                                    }
                                });
                            });
                        }
                    }
                }
            });
        },
        saveChartImage() {
            if (!this.chartInstance) return;

            const canvas = this.$refs.chartCanvas;
            const link = document.createElement('a');
            link.download = 'chart_' + this.queryDate + '.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        },
        exportTable(type) {
            let data = [];
            let filename = '';
            let headers = [];

            const statusHeaders = [
                this.$t('order_status_unpaid'),
                this.$t('order_status_awaiting_shipment'),
                this.$t('order_status_awaitingcollection'),
                this.$t('order_status_awaiting_transit'),
                this.$t('order_status_intransit'),
                this.$t('order_status_delivered'),
                this.$t('order_status_completed'),
                this.$t('order_status_cancelled'),
                this.$t('order_status_returning')
            ];

            switch (type) {
                case 'platform':
                    filename = 'report_by_platform_' + this.queryDate;
                    headers = [this.$t('platform'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedPlatform.map(item => [
                        item.platform_name,
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
                case 'channel':
                    filename = 'report_by_channel_' + this.queryDate;
                    headers = [this.$t('channel'), this.$t('platform'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedChannel.map(item => [
                        item.channel_name || 'Channel ' + item.channel_id,
                        item.platform_name || '',
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
                case 'user':
                    filename = 'report_by_user_' + this.queryDate;
                    headers = [this.$t('user'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedUser.map(item => [
                        item.user_name || 'User ' + item.user_id,
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
                case 'products':
                    filename = 'report_top_products_' + this.queryDate;
                    headers = [this.$t('product'), this.$t('platform'), this.$t('quantity'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedProducts.map(item => [
                        item.bo_product_name,
                        item.platform_name || '',
                        item.sum_quantity || 0,
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
                case 'wms':
                    filename = 'report_top_wms_' + this.queryDate;
                    headers = [this.$t('wms_product'), this.$t('quantity'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedWms.map(item => [
                        item.wms_product_name,
                        item.sum_quantity || 0,
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
                case 'packer':
                    filename = 'report_by_packer_' + this.queryDate;
                    headers = [this.$t('packer'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedPacker.map(item => [
                        item.packer_user_name || 'Packer ' + item.packer_user_id,
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
                case 'packer_storage':
                    filename = 'report_by_packer_storage_' + this.queryDate;
                    headers = [this.$t('storage'), this.$t('count_all'), ...statusHeaders, this.$t('total_amount'), this.$t('total_pay_amount')];
                    data = this.sortedPackerStorage.map(item => [
                        item.storage_name || 'Storage ' + item.storage_id,
                        this.countAll(item.summary),
                        item.summary.count_unpaid || 0,
                        item.summary.count_awaiting_shipment || 0,
                        item.summary.count_awaiting_collection || 0,
                        item.summary.count_awaiting_transit || 0,
                        item.summary.count_in_transit || 0,
                        item.summary.count_delivered || 0,
                        item.summary.count_completed || 0,
                        item.summary.count_cancelled || 0,
                        item.summary.count_returning || 0,
                        item.summary.sum_amount || 0,
                        item.summary.sum_pay_amount || 0
                    ]);
                    break;
            }

            // Create CSV content
            let csvContent = '\uFEFF'; // BOM for UTF-8
            csvContent += headers.join(',') + '\n';
            data.forEach(row => {
                csvContent += row.map(cell => {
                    if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
                        return '"' + cell.replace(/"/g, '""') + '"';
                    }
                    return cell;
                }).join(',') + '\n';
            });

            // Download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename + '.csv';
            link.click();
            URL.revokeObjectURL(link.href);
        }
    },
    mounted() {
        this.queryDate = moment().format("YYYY-MM-DD");
        this.handleSearch();
    },
    beforeDestroy() {
        if (this.chartInstance) {
            this.chartInstance.destroy();
        }
    }
};
</script>
<style>
.modal-fullscreen-custom {
    max-width: 95vw !important;
    width: 95vw !important;
    margin: 1.75rem auto;
}
.modal-fullscreen-custom .modal-content {
    min-height: 90vh;
}
.modal-fullscreen-custom .modal-body {
    display: flex;
    flex-direction: column;
    flex: 1;
}
</style>
<style scoped>
.chart-container-fullscreen {
    position: relative;
    width: 100%;
    height: calc(90vh - 150px);
    min-height: 500px;
}
</style>
